import { createFileRoute } from "@tanstack/react-router";
import { ServiceDetail } from "@/components/ServiceDetail";
import { serviceFaqs } from "@/data/serviceFaqs";
import { serviceGraph, abs } from "@/lib/serviceSchema";
import img from "@/assets/commercial-av-denver.jpg";

const bullets = [
  "Commercial AV design, engineering drawings, and installation",
  "Conference rooms, boardrooms, and huddle spaces on every major platform",
  "LED video walls, lobby displays, and digital signage networks",
  "Distributed audio and paging for restaurants, gyms, and retail",
  "Church, auditorium, and multipurpose room sound and video",
  "Commercial networking, structured cabling, and camera systems",
  "Service-level agreements with remote monitoring and on-site response",
];

export const Route = createFileRoute("/services/commercial-av")({
  head: () => ({
    meta: [
      { title: "Commercial AV Denver | Business Audio Video Installation" },
      { name: "description", content: "Commercial AV in Denver — conference rooms, video walls, digital signage, and distributed audio for offices, restaurants, gyms, and churches. Free site walk." },
      { name: "robots", content: "index, follow" },
      { name: "keywords", content: "commercial AV Denver, commercial audio video installation Denver, business AV installation Denver, commercial AV integrator Denver, digital signage Denver, video wall installation Denver, restaurant audio system Denver, church AV installation Denver, gym sound system Denver, commercial audio installation Denver, office AV Denver, commercial structured cabling Denver" },
      { property: "og:title", content: "Commercial AV Denver — Apex AV" },
      { property: "og:description", content: "Commercial audio video design and installation for Denver offices, restaurants, gyms, churches, and retail." },
      { property: "og:image", content: abs(img) },
      { name: "twitter:image", content: abs(img) },
      { property: "og:url", content: "https://apexav.com/services/commercial-av" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Commercial AV Denver — Apex AV" },
      { name: "twitter:description", content: "Commercial audio video design and installation for Denver businesses." },
    ],
    links: [{ rel: "canonical", href: "https://apexav.com/services/commercial-av" }, { rel: "preload", as: "image", href: img, fetchPriority: "high" } as never],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceGraph({
            slug: "commercial-av",
            name: "Commercial AV Installation",
            alternateName: ["Commercial Audio Video Denver", "Business AV Installation Denver"],
            description:
              "Commercial audio video design and installation across the Denver metro — conference rooms, video walls, digital signage, distributed audio, and commercial networking.",
            pageTitle: "Commercial AV Denver | Business Audio Video Installation",
            pageDescription:
              "Commercial AV in Denver — conference rooms, video walls, digital signage, and distributed audio for offices, restaurants, gyms, and churches.",
            image: img,
            faqs: serviceFaqs["commercial-av"],
            serviceType: "Commercial AV Installation",
            breadcrumbName: "Commercial AV Denver",
            offerCatalog: bullets,
          })
        ),
      },
    ],
  }),
  component: () => (
    <ServiceDetail
      faqs={serviceFaqs["commercial-av"]}
      topics={["av-installation", "networking", "security", "smart-home"]}
      eyebrow="Commercial AV"
      title="Commercial AV built for business hours."
      intro={<>Full-scope <strong>commercial AV in Denver</strong> — meeting rooms, <strong>video walls and digital signage</strong>, distributed audio, commercial networking, and cameras, designed and installed so your staff never has to troubleshoot a room before a client walks in.</>}
      image={img}
      imageAlt="Commercial AV installation with a video wall and boardroom displays in a downtown Denver office"
      brands={["sony", "samsung", "lg", "sanus", "ubiquiti"]}
      bullets={bullets}
      features={[
        { title: "Drawings first", desc: "Signal-flow and rack elevations before anything gets ordered, so your GC and IT team are working from the same plan." },
        { title: "Built to be handed off", desc: "Every room behaves the same way, labeled and documented, with training for the people who use it daily." },
        { title: "Backed by an SLA", desc: "Remote monitoring flags a failing display or mic before your team does, with guaranteed on-site response." },
      ]}
    />
  ),
});
